import { Minus, Plus } from "lucide-react";
import { ChoiceField, PanelField } from "@/components/signs/Fields";
import { VIEW_LABELS, garmentColors } from "@/components/shirts/ApparelCanvas";
import SizeMatrix from "@/components/shirts/SizeMatrix";
import TextPanel from "@/components/shirts/TextPanel";
import DecorationPanel from "@/components/shirts/DecorationPanel";

const slug = (s) => String(s || "").replace(/\W+/g, "-").toLowerCase();
const Swatch = ({ hex }) => <span className="mr-1.5 inline-block h-3 w-3 rounded-full border border-zinc-300 align-[-1px]" style={{ background: hex }} />;

function ColorField({ product, cfg, theme }) {
    const colors = garmentColors(product);
    const choices = colors.map((c) => ({ id: slug(c.name), label: c.name, hex: c.hex }));
    return (
        <ChoiceField label="Color" theme={theme} testid="field-color" choices={choices} value={slug(cfg.color?.name)}
            onChange={(id) => cfg.setColor(colors.find((c) => slug(c.name) === id))}
            help={colors.length === 1 ? "Every color is its own product — pick another color on the Shirts page." : undefined}
            render={(c) => <><Swatch hex={c.hex} />{c.label}</>} />
    );
}

function SizesField({ product, cfg, theme }) {
    const total = Object.values(cfg.sizes).reduce((n, v) => n + (Number(v) || 0), 0);
    const min = product.pricing?.min_quantity || product.min_quantity;
    return (
        <PanelField label="Sizes & qty" theme={theme} testid="field-sizes" wide="w-[460px] max-w-[92vw]" attention={!total}
            value={total ? `${total} pcs` : "Pick sizes to start"}
            dot={min && total && total < min ? <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" data-testid="sizes-under-min" /> : null}>
            <SizeMatrix product={product} sizes={cfg.sizes} setSize={cfg.setSize} theme={theme} />
        </PanelField>
    );
}

/** How the picked image sits on the garment. No inches: the design prints in proportion to each shirt size. */
function PrintSizeField({ product, cfg, theme }) {
    const view = cfg.view;
    const art = cfg.arts[view];
    const label = VIEW_LABELS[view];
    const area = (product.print_areas || {})[view] || { max_width_in: 15.5, max_height_in: 20 };
    const set = (patch) => cfg.setArt(view, { ...art, ...patch });
    const step = (d) => set({ widthIn: Math.min(area.max_width_in, Math.max(1, Math.round((art.widthIn + d) * 10) / 10)) });
    const btn = "grid h-7 w-7 place-items-center rounded-full border border-zinc-300 text-zinc-700 hover:border-zinc-500";
    return (
        <PanelField label={`Design size · ${label}`} theme={theme} testid="field-print-size" wide="w-[360px] max-w-[92vw]"
            blocked={art?.url ? null : `Upload a ${label.toLowerCase()} image first.`}
            value={art?.url ? "Smaller / bigger" : "No image"}>
            {art?.url && (
                <div className="space-y-2.5 p-1.5">
                    <div className="flex items-center gap-2">
                        <button type="button" className={btn} onClick={() => step(-0.5)} data-testid={`art-smaller-${view}`}><Minus size={13} /></button>
                        <input type="range" min="1" max={area.max_width_in} step="0.1" value={Math.min(art.widthIn, area.max_width_in)}
                            onChange={(e) => set({ widthIn: Number(e.target.value) })} className="flex-1" style={{ accentColor: theme?.primary }} data-testid={`art-width-slider-${view}`} />
                        <button type="button" className={btn} onClick={() => step(0.5)} data-testid={`art-bigger-${view}`}><Plus size={13} /></button>
                    </div>
                    <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                        <span>Smaller</span>
                        <button type="button" onClick={() => set({ x: 0, y: 0 })} className="font-bold normal-case tracking-normal underline" style={{ color: theme?.ink || theme?.primary }} data-testid={`art-center-${view}`}>Re-center</button>
                        <span>Bigger</span>
                    </div>
                    <p className="rounded-lg bg-zinc-50 px-2.5 py-2 text-[11px] leading-snug text-zinc-600" data-testid={`art-size-note-${view}`}>
                        <b className="text-zinc-800">The shirt on screen is an example.</b> Your design prints in proportion to each size you order —
                        smaller on a youth shirt, bigger on a 3XL — so we do not quote it in inches. Set how much of the print area it fills
                        here, then drag it on the garment to place it.
                    </p>
                </div>
            )}
        </PanelField>
    );
}

function TextField({ cfg, theme, selText, setSelText }) {
    const n = cfg.viewTexts.length;
    return (
        <PanelField label={`Text · ${VIEW_LABELS[cfg.view]}`} theme={theme} testid="field-text" wide="w-[400px] max-w-[92vw]"
            value={n ? `${n} layer${n > 1 ? "s" : ""}` : "None"}>
            <TextPanel texts={cfg.viewTexts} addText={cfg.addText} setText={cfg.setText} removeText={cfg.removeText}
                selected={selText} onSelect={setSelText} theme={theme} />
        </PanelField>
    );
}

function DecoField({ product, cfg, theme }) {
    const embroidery = product.decoration === "embroidery";
    const tier = (product.pricing?.stitch_tiers || []).find((t) => t.id === cfg.deco.stitch_tier);
    const inks = Object.values(cfg.deco.colors || {}).reduce((n, v) => n + (Number(v) || 1), 0) || cfg.locations.length;
    const value = embroidery
        ? `${tier?.label || "Pick a tier"}${cfg.deco.digitizing !== false ? " · digitize" : ""}`
        : cfg.locations.length ? `${inks} color${inks > 1 ? "s" : ""}` : "Upload art first";
    return (
        <PanelField label={embroidery ? "Stitch count" : "Ink colors"} theme={theme} testid="field-deco" wide="w-[380px] max-w-[92vw]" value={value}>
            <DecorationPanel product={product} locations={cfg.locations} deco={cfg.deco} setDeco={cfg.setDeco} quote={cfg.quote} theme={theme} />
        </PanelField>
    );
}

/** The row of white fields along the bottom of the apparel designer. */
export default function ShirtDock({ product, cfg, theme, selText, setSelText }) {
    return (
        <div className="border-t border-zinc-200 bg-[#f7f7f8] px-4 py-3 sm:px-6" data-testid="shirt-dock">
            <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-wrap lg:justify-center lg:overflow-visible lg:pb-0">
                <ColorField product={product} cfg={cfg} theme={theme} />
                <SizesField product={product} cfg={cfg} theme={theme} />
                <PrintSizeField product={product} cfg={cfg} theme={theme} />
                <TextField cfg={cfg} theme={theme} selText={selText} setSelText={setSelText} />
                {product.decoration && <DecoField product={product} cfg={cfg} theme={theme} />}
            </div>
        </div>
    );
}
